<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import { usePaymentApi } from '@store/api/paymentApi';
import type { PromoCode } from '@store/api/interfaces';
import { gtmPromoApply } from '@composables/gtm';
import CoreIcon from '@core/core-icon.vue';

const { t } = useI18n();
const toast = useToast();
const paymentApi = usePaymentApi();

const props = defineProps<{
	requireApply?: boolean;
	initialCode?: string;
}>();

const emit = defineEmits<{
	(e: 'applied', result: PromoCode): void;
	(e: 'checked', code: string): void;
	(e: 'cleared'): void;
}>();

const inputValue = ref(props.initialCode || '');
const isLoading = ref(false);
const isApplied = ref(!!props.initialCode);
const appliedCode = ref(props.initialCode || '');
const hasError = ref(false);

const showApplyButton = computed((): boolean => {
	return inputValue.value.trim().length > 0 && !isApplied.value;
});

const applyPromoCode = async () => {
	const code = inputValue.value.trim();
	if (!code || isLoading.value) return;

	isLoading.value = true;
	hasError.value = false;

	try {
		// Step 1: check if promo code exists
		const checkResult = await paymentApi.http.checkPromoCode(code);
		if (!checkResult.exists) {
			hasError.value = true;
			toast.error(t('payment.promo_invalid'), { position: 'top' });
			return;
		}

		if (props.requireApply) {
			// Authenticated flow: apply promo via API
			const result = await paymentApi.http.applyPromoCode(code);
			isApplied.value = true;
			appliedCode.value = result.code;
			inputValue.value = result.code;
			toast.success(t('payment.promo_applied'), { position: 'top' });
			gtmPromoApply(result.code);
			emit('applied', result);
		} else {
			// Unauthenticated flow: just confirm it exists, pass code along
			isApplied.value = true;
			appliedCode.value = code;
			inputValue.value = code;
			toast.success(t('payment.promo_applied'), { position: 'top' });
			gtmPromoApply(code);
			emit('checked', code);
		}
	} catch (error) {
		hasError.value = true;
		// The API wrappers throw their own errors, not ApiError: read the code there
		const code = paymentApi.methods.promoRejectionCode(error);
		toast.error(code ? t(`errorCode.${code}`) : t('payment.promo_invalid'), { position: 'top' });
	} finally {
		isLoading.value = false;
	}
};

const clearPromoCode = () => {
	inputValue.value = '';
	isApplied.value = false;
	appliedCode.value = '';
	hasError.value = false;
	emit('cleared');
};

const onKeydown = (e: KeyboardEvent) => {
	if (e.key === 'Enter') {
		e.preventDefault();
		applyPromoCode();
	}
};

const setCode = (code: string) => {
	inputValue.value = code;
};

defineExpose({ setCode, applyPromoCode });
</script>

<template>
	<div class="promo-input">
		<div
			class="promo-input__field"
			:class="{
				'promo-input__field_focused': showApplyButton,
				'promo-input__field_applied': isApplied,
				'promo-input__field_error': hasError
			}"
		>
			<core-icon
				:name="'ticket-sale'"
				:view-box="'0 0 24 24'"
				class="promo-input__icon"
			/>

			<template v-if="isApplied">
				<span class="promo-input__code">{{ appliedCode }}</span>
				<button
					class="promo-input__clear"
					@click="clearPromoCode"
				>
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM9.70711 8.29289C9.31658 7.90237 8.68342 7.90237 8.29289 8.29289C7.90237 8.68342 7.90237 9.31658 8.29289 9.70711L10.5858 12L8.29289 14.2929C7.90237 14.6834 7.90237 15.3166 8.29289 15.7071C8.68342 16.0976 9.31658 16.0976 9.70711 15.7071L12 13.4142L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L13.4142 12L15.7071 9.70711C16.0976 9.31658 16.0976 8.68342 15.7071 8.29289C15.3166 7.90237 14.6834 7.90237 14.2929 8.29289L12 10.5858L9.70711 8.29289Z" class="promo-input__clear-path"/>
					</svg>
				</button>
			</template>

			<template v-else>
				<input
					v-model="inputValue"
					type="text"
					class="promo-input__input"
					:placeholder="t('payment.promo_placeholder')"
					@keydown="onKeydown"
					@input="hasError = false"
				/>
				<button
					v-if="showApplyButton"
					class="promo-input__apply"
					:disabled="isLoading"
					@click="applyPromoCode"
				>
					{{ t('payment.promo_apply') }}
				</button>
			</template>
		</div>
	</div>
</template>

<style lang="scss">
.promo-input {
	&__field {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		height: 5.2rem;
		padding: 0 0.8rem 0 1.6rem;
		border-radius: 1.6rem;
		border: 1px solid rgba(0, 28, 51, 0.12);
		transition: border-color 0.2s ease;

		@include theme-dark {
			border-color: rgba(255, 255, 255, 0.12);
		}

		@include min-desktop {
			height: 6.2rem;
		}

		&_focused {
			border-color: var(--color-purple);
		}

		&_error {
			border-color: #ff2c58;
		}

		&_applied {
			border-color: rgba(0, 28, 51, 0.12);

			@include theme-dark {
				border-color: rgba(255, 255, 255, 0.12);
			}

			.promo-input__icon {
				color: var(--color-purple);
				opacity: 1;
			}
		}
	}

	&__icon {
		flex: 0 0 auto;
		width: 2.4rem;
		height: 2.4rem;
		color: var(--color-text);
		opacity: 0.5;
		stroke: currentColor;
		fill: none;

		circle {
			fill: currentColor;
			stroke: none;
		}
	}

	&__input {
		// min-width: 0 — without it the flex item can't shrink below the
		// browser's intrinsic <input> width (177px in Blink, 202px in WebKit),
		// so the apply button gets pushed out of the row on iOS.
		flex: 1 1 auto;
		min-width: 0;
		width: 100%;
		border: none;
		background: none;
		outline: none;
		@include font-18-pro-medium;
		color: var(--color-text);

		&::placeholder {
			color: rgba(0, 28, 51, 0.5);

			@include theme-dark {
				color: rgba(255, 255, 255, 0.5);
			}
		}

		@include max-desktop {
			@include font-16-pro-medium;
		}
	}

	&__code {
		flex: 1 1 auto;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		@include font-18-pro-medium;
		color: var(--color-text);

		@include max-desktop {
			@include font-16-pro-medium;
		}
	}

	&__apply {
		flex: 0 0 auto;
		height: 4.2rem;
		padding: 0 1.6rem;
		border-radius: 1.2rem;
		cursor: pointer;
		@include font-16-pro-regular;
		@include button-primary-surface;
		font-weight: 500;
	}

	&__clear {
		flex: 0 0 auto;
		width: 2.4rem;
		height: 2.4rem;
		border: none;
		background: none;
		cursor: pointer;
		padding: 0;

		&:hover { opacity: 0.8; }
	}

	&__clear-path {
		fill: rgba(0, 28, 51, 0.3);

		@include theme-dark {
			fill: rgba(255, 255, 255, 0.3);
		}
	}
}
</style>
